import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import GiveawayDetails from './GiveawayDetails';
import WinnerRoll from './WinnerRoll';
import EditGiveaway from './EditGiveaway';

const GiveawayCard = ({ giveaway, hasEntered, onEntryComplete, onViewAnalytics }) => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [showDetails, setShowDetails] = useState(false);
  const [showRollModal, setShowRollModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [hostTrust, setHostTrust] = useState(null);

  const isHost = isAuthenticated && user && String(giveaway.host.userId) === String(user._id);
  const hasEnded = new Date() > new Date(giveaway.endDate);
  const isOngoing = giveaway.status === 'ongoing' && !hasEnded;
  const isClosed = giveaway.status === 'closed';
  const hasWinner = giveaway.rollLogs && giveaway.rollLogs.length > 0;

  // Fetch host trust badge
  useEffect(() => {
    const fetchHostTrust = async () => {
      try {
        const response = await axios.get(`/api/vouches/trust-badge/${giveaway.host.userId}`);
        setHostTrust(response.data.data);
      } catch (error) {
        console.error('Error fetching host trust:', error);
      }
    };
    
    if (giveaway.host.userId) {
      fetchHostTrust();
    }
  }, [giveaway.host.userId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = () => {
    const now = new Date();
    const end = new Date(giveaway.endDate);
    const diff = end - now;

    if (diff <= 0) return 'Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  return (
    <>
      <div
        className={`giveaway-card ${hasEntered ? 'entered' : ''}`}
        onClick={() => setShowDetails(true)}
      >
        {/* Banner */}
        <div
          className="giveaway-banner"
          style={{
            backgroundImage: giveaway.banner ? `url(${giveaway.banner.url})` : 'none'
          }}
        >
          {/* Status Badge */}
          <div className={`status-badge ${hasWinner ? 'rolled' : isClosed ? 'closed' : isOngoing ? 'live' : 'ended'}`}>
            {hasWinner ? '🏆 Rolled' : isClosed ? '🔒 Closed' : isOngoing ? '🔥 Live' : '⏱️ Ended'}
          </div>

          {/* Entry Status Badge */}
          {hasEntered && (
            <div className="entry-badge">
              ✓ Joined
            </div>
          )}

          {/* Giveaway ID */}
          <div className="giveaway-id-badge">
            {giveaway.giveawayId}
          </div>
        </div>

        {/* Content */}
        <div className="giveaway-content">
          {/* Title */}
          <h3 className="giveaway-title">
            {giveaway.title}
          </h3>

          {/* Host */}
          <p className="giveaway-host">
            🎭 Hosted by <strong>{giveaway.host.username}</strong>
          </p>

          {/* Trust Badge */}
          {hostTrust && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                background: `linear-gradient(135deg, ${hostTrust.trustColor}, ${hostTrust.trustColor}dd)`,
                padding: '6px 12px',
                borderRadius: '20px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                color: hostTrust.trustScore >= 50 ? 'white' : '#000',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <span>{hostTrust.trustBadge}</span>
                <span>{hostTrust.trustLevel}</span>
              </div>
              
              {hostTrust.isFastDelivery && (
                <div style={{
                  background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  <span>⚡</span>
                  <span>Fast Delivery</span>
                </div>
              )}

              {hostTrust.totalVouches > 0 && (
                <div style={{
                  background: 'rgba(255, 193, 7, 0.15)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.8rem',
                  color: '#FFC107'
                }}>
                  <span>⭐</span>
                  <span>{hostTrust.totalVouches} vouches</span>
                  {hostTrust.averageRating > 0 && (
                    <span>• {hostTrust.averageRating}/5</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Trusted Giveaway Host Badge (Legacy - only if no trust data) */}
          {!hostTrust && giveaway.host.trustedGiveawayHost && (
            <div className="trusted-giveaway-badge trusted">
              <span className="badge-icon">✓</span>
              <span className="badge-text">Trusted Giveaway Host</span>
            </div>
          )}
          {!hostTrust && !giveaway.host.trustedGiveawayHost && (
            <div className="trusted-giveaway-badge unverified">
              <span className="badge-icon">⚠</span>
              <span className="badge-text">This hoster has not been verified as a Trusted Giveaway Host yet</span>
            </div>
          )}

          {/* Prize */}
          <div className="giveaway-prize">
            <p className="giveaway-prize-label">
              🎁 Prize
            </p>
            <p className="giveaway-prize-text">
              {giveaway.prize}
            </p>
          </div>

          {/* Stats */}
          <div className="giveaway-stats">
            <div className="giveaway-stat">
              <p className="giveaway-stat-label">Participants</p>
              <p className="giveaway-stat-value">
                {giveaway.participantCount}
              </p>
            </div>
            <div className="giveaway-stat-right">
              <p className="giveaway-stat-label">
                {isOngoing ? 'Time Left' : 'Ended'}
              </p>
              <p className={`giveaway-stat-time ${isOngoing ? 'ongoing' : 'ended'}`}>
                {isOngoing ? getTimeRemaining() : formatDate(giveaway.endDate)}
              </p>
            </div>
          </div>

          {/* Winner Display */}
          {hasWinner && (giveaway.winners?.length > 0 || giveaway.currentWinner) && (
            <div className="giveaway-winner">
              <p className="giveaway-winner-label">
                🏆 {giveaway.winners?.length > 1 ? `Winners (${giveaway.winners.length})` : 'Winner'}
              </p>
              {giveaway.winners?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                  {giveaway.winners.map((winner, index) => (
                    <p key={winner.userId || index} className="giveaway-winner-name" style={{ margin: '2px 0' }}>
                      {giveaway.winners.length > 1 && `#${winner.position}: `}{winner.username}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="giveaway-winner-name">
                  {giveaway.currentWinner.username}
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="giveaway-actions">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDetails(true);
              }}
              className="view-details-btn"
            >
              {hasEntered ? 'View Details' : 'View & Enter'}
            </button>

            {/* Share Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const shareUrl = `${window.location.origin}/giveaways/${giveaway._id}`;
                if (navigator.share) {
                  navigator.share({
                    title: giveaway.title,
                    text: `Check out this giveaway: ${giveaway.title}`,
                    url: shareUrl
                  });
                } else {
                  navigator.clipboard.writeText(shareUrl).then(() => {
                    // Could add a toast notification here
                    alert('Direct link copied to clipboard!');
                  }).catch(() => {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = shareUrl;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    alert('Direct link copied to clipboard!');
                  });
                }
              }}
              className="share-btn"
            >
              🔗 Share
            </button>

            {/* Analytics Button (Host Only) */}
            {isHost && onViewAnalytics && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewAnalytics(giveaway._id);
                }}
                className="analytics-btn"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                📊 Analytics
              </button>
            )}

            {/* Edit Button (Host Only - While Ongoing) */}
            {isHost && isOngoing && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditModal(true);
                }}
                className="edit-btn"
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white'
                }}
              >
                ✏️ Edit
              </button>
            )}

            {/* Roll Button (Host Only) */}
            {isHost && hasEnded && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRollModal(true);
                }}
                className="roll-btn"
              >
                🎲 {hasWinner ? 'Reroll' : 'Roll'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDetails && (
        <GiveawayDetails
          giveaway={giveaway}
          hasEntered={hasEntered}
          isHost={isHost}
          onClose={() => setShowDetails(false)}
          onEntryComplete={() => {
            setShowDetails(false);
            onEntryComplete();
          }}
          onViewAnalytics={onViewAnalytics}
        />
      )}

      {showEditModal && (
        <EditGiveaway
          giveaway={giveaway}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            onEntryComplete();
          }}
        />
      )}

      {showRollModal && (
        <WinnerRoll
          giveaway={giveaway}
          onClose={() => setShowRollModal(false)}
          onRollComplete={() => {
            setShowRollModal(false);
            onEntryComplete();
          }}
        />
      )}
    </>
  );
};

export default GiveawayCard;
